import React, { useState, useEffect } from 'react';
import { api } from '../services/api';

interface ConditionalReminderConfig {
  id?: string;
  link_id: string;
  enabled: boolean;
  check_delay_minutes: number;
  attended_email_subject: string;
  attended_email_body: string;
  missed_email_subject: string;
  missed_email_body: string;
}

interface ConditionalReminderSettingsProps {
  linkId: string;
  linkName: string;
  onSave?: () => void;
}

interface Placeholders {
  [key: string]: string;
}

const ConditionalReminderSettings: React.FC<ConditionalReminderSettingsProps> = ({
  linkId,
  linkName: _linkName,
  onSave
}) => {
  const [config, setConfig] = useState<ConditionalReminderConfig | null>(null);
  const [placeholders, setPlaceholders] = useState<Placeholders>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewType, setPreviewType] = useState<'attended' | 'missed'>('attended');
  const [previewHtml, setPreviewHtml] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    loadConfig();
  }, [linkId]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/scheduling-links/${linkId}/conditional-reminders`);
      setConfig(response.data.config);
      setPlaceholders(response.data.placeholders || {});
    } catch (error: any) {
      console.error('Failed to load conditional reminder config:', error);
      const errorMessage = error?.message || error?.error || 'Failed to load conditional reminder settings. Please try again.';
      setError(errorMessage);
      setConfig(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config) return;

    try {
      setSaving(true);
      await api.put(`/scheduling-links/${linkId}/conditional-reminders`, config);
      
      if (onSave) {
        onSave();
      }
      
      alert('Conditional reminder settings saved successfully!');
    } catch (error: any) {
      console.error('Failed to save config:', error);
      alert(error.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async (type: 'attended' | 'missed') => {
    if (!config) return;

    try {
      setLoadingPreview(true);
      setPreviewType(type);
      
      const response = await api.post(`/scheduling-links/${linkId}/conditional-reminders/preview`, {
        type,
        subject: type === 'attended' ? config.attended_email_subject : config.missed_email_subject,
        body: type === 'attended' ? config.attended_email_body : config.missed_email_body
      });
      
      setPreviewHtml(response.data.html);
      setShowPreview(true);
    } catch (error: any) {
      console.error('Failed to generate preview:', error);
      alert(error.message || 'Failed to generate preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-primary)]"></div>
        <p className="mt-2" style={{ color: 'var(--color-gray-500)' }}>Loading settings...</p>
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className="p-6">
        <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-6 text-center">
          <div className="mb-4">
            <svg className="mx-auto h-12 w-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-navy)' }}>
            Failed to Load Conditional Reminder Settings
          </h3>
          <p className="mb-4" style={{ color: 'var(--color-gray-500)' }}>
            {error || 'Unable to load conditional reminder settings. This may be due to a database issue or missing configuration.'}
          </p>
          <div className="flex justify-center gap-3">
            <button
              onClick={loadConfig}
              disabled={loading}
              className="btn btn-primary"
            >
              {loading ? 'Retrying...' : 'Retry'}
            </button>
          </div>
          {error && (
            <details className="mt-4 text-left">
              <summary className="text-sm cursor-pointer" style={{ color: 'var(--color-gray-500)' }}>
                Technical Details
              </summary>
              <pre className="mt-2 p-3 bg-gray-900 rounded text-xs text-red-400 overflow-auto">
                {error}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 'var(--space-6)', display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <div>
        <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--color-navy)' }}>
          Conditional Reminders
        </h3>
        <p className="text-sm" style={{ color: 'var(--color-gray-500)' }}>
          Send different follow-up emails based on whether participants attended or missed the Teams meeting.
        </p>
      </div>

      <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-4)' }}>
        <div>
          <label htmlFor="enabled" className="font-medium" style={{ color: 'var(--color-navy)' }}>
            Enable Conditional Reminders
          </label>
          <p className="text-sm mt-1" style={{ color: 'var(--color-gray-500)' }}>
            Automatically check attendance and send follow-up emails
          </p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            id="enabled"
            checked={config.enabled}
            onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
            className="sr-only peer"
          />
          <div className="conditional-reminder-toggle"></div>
        </label>
      </div>

      {config.enabled && (
        <>
          <div className="form-group">
            <label className="form-label">
              Check Delay: {config.check_delay_minutes} minutes after meeting ends
            </label>
            <input
              type="range"
              min="15"
              max="120"
              step="5"
              value={config.check_delay_minutes}
              onChange={(e) => setConfig({ ...config, check_delay_minutes: parseInt(e.target.value) })}
              className="conditional-reminder-slider"
            />
            <div className="flex justify-between text-xs" style={{ color: 'var(--color-gray-500)', marginTop: 'var(--space-2)' }}>
              <span>15 min</span>
              <span>120 min (2 hours)</span>
            </div>
          </div>

          <div className="card" style={{ padding: 'var(--space-4)' }}>
            <h4 className="font-medium mb-2" style={{ color: 'var(--color-navy)' }}>Available Placeholders</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {Object.entries(placeholders).map(([key, description]) => (
                <div key={key} style={{ color: 'var(--color-gray-500)' }}>
                  <code className="px-2 py-1 rounded text-xs" style={{ backgroundColor: 'var(--color-gray-200)', color: 'var(--color-primary)' }}>
                    {key}
                  </code>
                  <span className="ml-2 text-xs">{description}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="card" style={{ padding: 'var(--space-4)', backgroundColor: 'var(--color-success-light)', borderColor: 'var(--color-success)' }}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium flex items-center" style={{ color: 'var(--color-navy)' }}>
                <span className="mr-2" style={{ color: 'var(--color-success)' }}>✓</span>
                When Attended
              </h4>
              <button
                type="button"
                onClick={() => handlePreview('attended')}
                disabled={loadingPreview}
                className="btn btn-secondary btn-sm"
              >
                {loadingPreview && previewType === 'attended' ? 'Loading...' : 'Preview'}
              </button>
            </div>

            <div className="form-group">
              <label className="form-label">
                Subject Line
              </label>
              <input
                type="text"
                value={config.attended_email_subject}
                onChange={(e) => setConfig({ ...config, attended_email_subject: e.target.value })}
                className="form-input"
                placeholder="Thank you for attending {{meeting_name}}"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Email Body
              </label>
              <textarea
                value={config.attended_email_body}
                onChange={(e) => setConfig({ ...config, attended_email_body: e.target.value })}
                rows={6}
                className="form-textarea"
                style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-sm)' }}
                placeholder="Thank you for joining {{meeting_name}} with {{host_name}}!&#10;&#10;We appreciate your time..."
              />
              <p className="form-hint">
                {config.attended_email_body.length} characters
              </p>
            </div>
          </div>

          <div className="card" style={{ padding: 'var(--space-4)', backgroundColor: 'var(--color-warning-light)', borderColor: 'var(--color-warning)' }}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium flex items-center" style={{ color: 'var(--color-navy)' }}>
                <span className="mr-2" style={{ color: 'var(--color-warning)' }}>!</span>
                When Missed
              </h4>
              <button
                type="button"
                onClick={() => handlePreview('missed')}
                disabled={loadingPreview}
                className="btn btn-secondary btn-sm"
              >
                {loadingPreview && previewType === 'missed' ? 'Loading...' : 'Preview'}
              </button>
            </div>

            <div className="form-group">
              <label className="form-label">
                Subject Line
              </label>
              <input
                type="text"
                value={config.missed_email_subject}
                onChange={(e) => setConfig({ ...config, missed_email_subject: e.target.value })}
                className="form-input"
                placeholder="We missed you at {{meeting_name}}"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Email Body
              </label>
              <textarea
                value={config.missed_email_body}
                onChange={(e) => setConfig({ ...config, missed_email_body: e.target.value })}
                rows={6}
                className="form-textarea"
                style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-sm)' }}
                placeholder="Hi {{recipient_name}},&#10;&#10;We noticed you were unable to attend {{meeting_name}}..."
              />
              <p className="form-hint">
                {config.missed_email_body.length} characters
              </p>
            </div>
          </div>
        </>
      )}

      <div className="flex justify-end space-x-3 pt-4" style={{ borderTop: '1px solid var(--color-gray-200)' }}>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="btn btn-primary"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {showPreview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="card max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--color-gray-200)' }}>
              <h3 className="text-lg font-semibold" style={{ color: 'var(--color-navy)' }}>
                Email Preview - {previewType === 'attended' ? 'Attended' : 'Missed'}
              </h3>
              <button
                onClick={() => setShowPreview(false)}
                className="btn btn-ghost btn-sm"
                style={{ padding: 'var(--space-2)' }}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConditionalReminderSettings;
